import { OnDemandData, ItemPaging, type OnDemandItem } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { Logger } from '@/utils';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';

export const useFavoriteOnDemandsStore = defineStore('FavoriteOnDemands', (): ExposeInterface => {
    const { 
        emptyOnDemandData
    } = useEmptyLine();

    const { $services } = useNuxtApp();
    let _numberPage: number = 1;

    const _favoriteOnDemands: Ref<OnDemandData | null> = ref(null);
    const getFavoriteOnDemands: ComputedRef<OnDemandData | null> = computed(() => _favoriteOnDemands.value);

    const fetchFavoriteOnDemands = async (): Promise<void> => {
        try {
            const response = await $services.favorites.getFavoritesOnDemand(1, AppConstants.ItemsInLine);
            _favoriteOnDemands.value = new OnDemandData(response.items, null, response.paging);
        } catch (error) {
            _favoriteOnDemands.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError);
        }
    };

    const fetchFavoriteOnDemandsNextPage = async (): Promise<void> => {
        try {
            _numberPage++;

            const response = await $services.favorites.getFavoritesOnDemand(_numberPage, AppConstants.ItemsInLine);
            if (!_favoriteOnDemands.value?.items || !response.items) {
                throw new Error('Favorite OnDemand is empty or response is empty');
            }
            const items: OnDemandItem[] = [..._favoriteOnDemands.value.items, ...response.items];
            const paging = new ItemPaging(response.paging?.itemsOnPage, _numberPage, response.paging?.totalItems, response.paging?.totalPages);
            _favoriteOnDemands.value = new OnDemandData(items, null, paging);
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    const _deletedIds: Ref<Set<string>> = ref(new Set());
    const getDeletedIdsOnDemands: ComputedRef<Set<string>> = computed(() => _deletedIds.value);
    const saveDeletedIds = (id: string): void => {
        _deletedIds.value.add(id);
    };
    const clearDeletedIds = (id: string): void => {
        if(_deletedIds.value.has(id)) {
            _deletedIds.value.delete(id);
        }
    }
    const clearAllDeletedIds = (): void => {
        _deletedIds.value.clear()
    }

    const clearHard = (): void => {
        _numberPage = 1;
        _favoriteOnDemands.value = null;
    };

    const clearSoft = (): void => {
        _numberPage = 1;
        if (!_favoriteOnDemands.value?.items) {
            throw new Error('Favorite OnDemand is empty');
        }
        const items = [..._favoriteOnDemands.value.items].slice(0, AppConstants.ItemsInLine);
        if (!_favoriteOnDemands.value.paging) {
            throw new Error('Favorite OnDemand paging is empty');
        }
        const paging = new ItemPaging(_favoriteOnDemands.value.paging.itemsOnPage, _numberPage, _favoriteOnDemands.value.paging.totalItems, _favoriteOnDemands.value.paging.totalPages);
        _favoriteOnDemands.value = new OnDemandData(items, null, paging);
    };


    return {
        getDeletedIdsOnDemands,
        getFavoriteOnDemands,
        fetchFavoriteOnDemands,
        fetchFavoriteOnDemandsNextPage,
        saveDeletedIds,
        clearDeletedIds,
        clearAllDeletedIds,
        clearHard,
        clearSoft
    };
});